import { isSupabaseConfigured, supabase } from './supabaseClient';
import { teamToSlug } from '../utils/sports';

export const SHOP_HEADER_KEY = 'shop';
export const DEFAULT_CROP = { x: 0.5, y: 0.5, scale: 1 };
export const PAGE_HEADER_ASPECT = 21 / 9;

/** Bento placement — larger slots for the headline sports. */
export const SPORT_HOME_LAYOUT = {
  'formula-series': 'hero',
  football: 'lead',
  basketball: 'accent',
  baseball: 'compact',
  volleyball: 'compact',
  cricket: 'compact',
  rugby: 'compact',
};

export const HOME_TILE_ASPECT = {
  hero: 1,
  lead: 16 / 9,
  accent: 16 / 9,
  compact: 16 / 9,
};

export function brandHeaderKey(brandSlug) {
  const slug = String(brandSlug || '').trim();
  if (!slug) return '';
  return `brand/${slug}`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function normalizeCrop(raw) {
  const x = Number(raw?.x);
  const y = Number(raw?.y);
  const scale = Number(raw?.scale);
  return {
    x: Number.isFinite(x) ? clamp(x, 0, 1) : DEFAULT_CROP.x,
    y: Number.isFinite(y) ? clamp(y, 0, 1) : DEFAULT_CROP.y,
    scale: Number.isFinite(scale) ? clamp(scale, 1, 8) : DEFAULT_CROP.scale,
  };
}

export function normalizeHeaderEntry(value) {
  if (typeof value === 'string') {
    const image_url = value.trim();
    return image_url
      ? { image_url, header_crop: { ...DEFAULT_CROP }, home_crop: { ...DEFAULT_CROP } }
      : null;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const image_url = String(value.image_url || value.url || '').trim();
  if (!image_url) return null;
  return {
    image_url,
    header_crop: normalizeCrop(value.header_crop),
    home_crop: normalizeCrop(value.home_crop),
  };
}

/** Normalize jsonb / null / JSON-string into a slug → header entry map. */
export function normalizeCollectionHeaders(raw) {
  let source = raw;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return {};
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
  const next = {};
  Object.entries(source).forEach(([key, value]) => {
    const entry = normalizeHeaderEntry(value);
    if (key && entry) next[key] = entry;
  });
  return next;
}

export function sportHeaderKey(sportSlug) {
  return String(sportSlug || '').trim();
}

export function leagueHeaderKey(sportSlug, leagueSlug) {
  const sport = sportHeaderKey(sportSlug);
  const league = String(leagueSlug || '').trim();
  if (!sport || !league) return '';
  return `${sport}/league/${league}`;
}

export function teamHeaderKey(sportSlug, leagueSlug, teamSlug) {
  const sport = sportHeaderKey(sportSlug);
  const team = String(teamSlug || '').trim();
  if (!sport || !team) return '';
  const league = String(leagueSlug || '').trim();
  if (league) return `${sport}/league/${league}/team/${team}`;
  return `${sport}/team/${team}`;
}

export function getCollectionHeader(headers, key) {
  if (!key) return null;
  return normalizeHeaderEntry(headers?.[key]);
}

export function getCollectionHeaderUrl(headers, key) {
  return getCollectionHeader(headers, key)?.image_url || '';
}

export function isSportHeaderKey(key) {
  return Object.prototype.hasOwnProperty.call(SPORT_HOME_LAYOUT, key);
}

export function getCropFrames(key) {
  const frames = [];
  if (isSportHeaderKey(key)) {
    const layout = SPORT_HOME_LAYOUT[key];
    frames.push({
      id: 'home',
      cropKey: 'home_crop',
      label: 'Homepage',
      hint: 'The tile on the home page index',
      aspect: HOME_TILE_ASPECT[layout] || HOME_TILE_ASPECT.compact,
    });
  }
  frames.push({
    id: 'header',
    cropKey: 'header_crop',
    label: 'Page header',
    hint: 'The banner on the collection page',
    aspect: PAGE_HEADER_ASPECT,
  });
  return frames;
}

/**
 * CSS for a cropped, cover-fitted background layer.
 * x/y are the focal point (0–1); scale is zoom relative to cover.
 */
export function cropMediaStyle(crop) {
  const { x, y, scale } = normalizeCrop(crop);
  return {
    backgroundPosition: `${x * 100}% ${y * 100}%`,
    backgroundSize: 'cover',
    transform: scale > 1.001 ? `scale(${scale})` : undefined,
    transformOrigin: `${x * 100}% ${y * 100}%`,
  };
}

function headerCandidates({ sportSlug = '', leagueSlug = '', teamSlug = '' } = {}) {
  const sport = sportHeaderKey(sportSlug);
  const league = String(leagueSlug || '').trim();
  const teamNormalized = teamToSlug(teamSlug);

  const candidates = [];
  if (sport && teamNormalized) {
    if (league) candidates.push(teamHeaderKey(sport, league, teamNormalized));
    candidates.push(teamHeaderKey(sport, '', teamNormalized));
  }
  if (sport && league) {
    candidates.push(leagueHeaderKey(sport, league));
  }
  if (sport) {
    candidates.push(sportHeaderKey(sport));
  }
  return candidates;
}

export function resolveCollectionHeaderEntry(
  headers,
  { sportSlug = '', leagueSlug = '', teamSlug = '' } = {}
) {
  for (const key of headerCandidates({ sportSlug, leagueSlug, teamSlug })) {
    const entry = getCollectionHeader(headers, key);
    if (entry) return entry;
  }
  return null;
}

/**
 * Most-specific header wins: team → league → sport.
 * Falls back across key variants so older sport-only uploads still work.
 */
export function resolveCollectionHeader(
  headers,
  { sportSlug = '', leagueSlug = '', teamSlug = '' } = {}
) {
  return resolveCollectionHeaderEntry(headers, { sportSlug, leagueSlug, teamSlug })
    ?.image_url || '';
}

export async function fetchCollectionHeaders() {
  if (!isSupabaseConfigured) return {};

  const { data, error } = await supabase
    .from('homepage_settings')
    .select('collection_headers')
    .eq('id', 1)
    .maybeSingle();

  if (error) {
    if (/collection_headers/i.test(error.message || '')) return {};
    throw error;
  }

  return normalizeCollectionHeaders(data?.collection_headers);
}
